import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { toast } from "@outpave/ds";
import {
  ADD_SETTING_POLICY,
  DELETE_SETTING_POLICY,
  UPDATE_SETTING_POLICY,
} from "../graphql/settingPolicy";
import SettingPolicyType from "../enums/SettingPolicyType";
import Role from "../enums/Role";
import approvals from "../validations/approvals";
import { useTranslation } from "react-i18next";
import { ApprovalPolicy } from "./settingPolicy.types";

/** The shape the approvals form edits. */
type ApprovalFormValues = {
  userRole: string;
  workflowSettings: string;
};

interface UseSettingPolicyFormProps {
  policy?: ApprovalPolicy;
  settingPolicyType: SettingPolicyType;
  onCompleted?: (id?: string) => void;
  onError?: (error: unknown, id?: string) => void;
}

const useSettingPolicyForm = ({
  policy,
  settingPolicyType,
  onCompleted,
  onError,
}: UseSettingPolicyFormProps) => {
  const { t } = useTranslation();
  // Mutations
  const [addSettingPolicy, { loading: loadingAddingPolicy }] = useMutation(
    ADD_SETTING_POLICY,
    {
      onCompleted: (data) => {
        if (onCompleted) onCompleted(data.addSettingPolicy.id);
        toast.success(t("Policy added successfully"));
      },
      onError: (err) => {
        if (onError) onError(err);
        toast.error(err.message);
      },
    },
  );

  const [updateSettingPolicy, { loading: loadingUpdatingPolicy }] = useMutation(
    UPDATE_SETTING_POLICY,
  );

  const [deleteSettingPolicy, { loading: deletingPolicy }] = useMutation(
    DELETE_SETTING_POLICY,
  );

  // Handlers
  const handleUpdateSettingPolicy = ({
    policy,
    newRoles,
  }: {
    policy: ApprovalPolicy;
    newRoles?: Role[];
  }) => {
    const input: { approvalRoles?: Role[] } = {};

    switch (settingPolicyType) {
      case SettingPolicyType.WORKFLOW_APPROVAL:
      case SettingPolicyType.INVOICE_APPROVAL:
      case SettingPolicyType.EXPENSE_REIMBURSEMENT:
      case SettingPolicyType.PAYMENT_APPROVAL:
        if (newRoles) {
          input.approvalRoles = newRoles;
        }
        break;
      default:
        return;
    }

    updateSettingPolicy({
      variables: {
        id: policy.id,
        userRole: policy.userRole,
        input,
        isForceUpdate: true,
      },
    })
      .then(() => {
        if (onCompleted) onCompleted(policy.id);
        toast.success(t("Policy updated successfully"));
      })
      .catch((err) => {
        if (onError) onError(err, policy.id);
        toast.error(err.message);
      });
  };

  const handleDeletePolicy = (id: string) => {
    deleteSettingPolicy({
      variables: { id },
    })
      .then(() => {
        if (onCompleted) onCompleted(id);
        toast.success(t("Policy removed successfully"));
      })
      .catch((err) => {
        if (onError) onError(err, id);
        toast.error(err.message);
      });
  };

  // Formik setup
  const getInitialValues = () => {
    switch (settingPolicyType) {
      case SettingPolicyType.WORKFLOW_APPROVAL:
      case SettingPolicyType.INVOICE_APPROVAL:
      case SettingPolicyType.EXPENSE_REIMBURSEMENT:
      default:
        return {
          userRole: policy?.userRole || "",
          workflowSettings: policy?.workflowSettings || "",
        };
    }
  };

  const getValidationSchema = () => {
    switch (settingPolicyType) {
      case SettingPolicyType.WORKFLOW_APPROVAL:
      case SettingPolicyType.INVOICE_APPROVAL:
      case SettingPolicyType.EXPENSE_REIMBURSEMENT:
      default:
        return approvals;
    }
  };

  const getInputValues = (values: ApprovalFormValues) => {
    switch (settingPolicyType) {
      case SettingPolicyType.WORKFLOW_APPROVAL:
      case SettingPolicyType.INVOICE_APPROVAL:
      case SettingPolicyType.EXPENSE_REIMBURSEMENT:
      default:
        return { approvalRoles: getApprovalRoles(values.workflowSettings) };
    }
  };

  const getApprovalRoles = (workflowSettings: string) => {
    switch (workflowSettings) {
      case "administratorApprovalOnly":
        return [Role.Admin];
      case "managerApprovalOnly":
        return [Role.Manager];
      case "managerAndAccountant":
        return [Role.Manager, Role.Accountant];
      case "managerAndAdministrators":
        return [Role.Manager, Role.Admin];
      case "noApprovalsRequired":
        return [];
      default:
        return [];
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      const input = getInputValues(values);

      addSettingPolicy({
        variables: {
          settingPolicyType,
          userRole: values.userRole,
          input,
          isForceUpdate: true,
        },
      });
    },
  });

  const isAnyLoading =
    loadingAddingPolicy || loadingUpdatingPolicy || deletingPolicy;

  return {
    formik,
    isAnyLoading,
    handleDeletePolicy,
    handleUpdateSettingPolicy,
  };
};

export default useSettingPolicyForm;
